import { useState } from "react";
import { Button } from "@/components/ui/Button";
import {
  listAudit,
  listBoxes,
  stockForProduct,
  getSerialPolicy,
  type InventoryProduct,
} from "@/lib/inventory/api";
import { useInventoryText } from "@/lib/inventory/i18n";
import { PolicyApproval } from "./InventoryReceive";
import {
  ErrorNotice,
  Field,
  Loading,
  Pager,
  Panel,
  ProductPicker,
  conditionText,
  policyText,
  selectClass,
  statusText,
  useRead,
} from "./shared";

export function InventoryAudit({ boxId }: { boxId?: string }) {
  const t = useInventoryText();
  const [offset, setOffset] = useState(0);
  const audit = useRead(() => listAudit(boxId, 25, offset), [boxId, offset]);
  return (
    <Panel title={boxId ? t.history : t.audit}>
      <Button disabled={audit.busy} variant="secondary" onClick={audit.refresh}>
        {t.retry}
      </Button>
      {boxId && <p className="font-mono">{boxId}</p>}
      {audit.busy && <Loading />}
      <ErrorNotice error={audit.error} />
      {audit.data && (
        <>
          <ol className="divide-y divide-border">
            {audit.data.events.map((event, index) => (
              <li
                className="space-y-2 py-3"
                key={String(event.event_id ?? index)}
              >
                <p>
                  <code>{event.event_id ?? "—"}</code> · {t.event}:{" "}
                  {event.event_type || event.kind || "—"}
                </p>
                <p>
                  {t.actor}: {event.actor || "—"} · {t.time}:{" "}
                  {event.created_at || "—"}
                </p>
                {event.reason && (
                  <p className="whitespace-pre-wrap">
                    {t.reason}: {event.reason}
                  </p>
                )}
                <details>
                  <summary className="cursor-pointer">{t.details}</summary>
                  <pre className="overflow-auto whitespace-pre-wrap break-words text-xs">
                    {JSON.stringify(event, null, 2)}
                  </pre>
                </details>
              </li>
            ))}
          </ol>
          {audit.data.events.length === 0 && <p>{t.empty}</p>}
          <Pager
            offset={offset}
            total={audit.data.total}
            limit={25}
            onChange={setOffset}
            busy={audit.busy}
          />
        </>
      )}
    </Panel>
  );
}
export function InventoryBoxes({ onOpen }: { onOpen: (id: string) => void }) {
  const t = useInventoryText();
  const [status, setStatus] = useState("");
  const [offset, setOffset] = useState(0);
  const result = useRead(
    () => listBoxes(status || undefined, 50, offset),
    [status, offset],
  );
  const statuses = ["UNASSIGNED", "ASSIGNED", "VOIDED"];
  return (
    <Panel title={t.boxes}>
      <div className="flex flex-wrap items-end gap-3">
        <Field label={t.status}>
          <select
            className={selectClass}
            value={status}
            disabled={result.busy}
            onChange={(e) => {
              setStatus(e.target.value);
              setOffset(0);
            }}
          >
            <option value="">{t.all}</option>
            {statuses.map((s) => (
              <option key={s} value={s}>
                {statusText(t, s)}
              </option>
            ))}
          </select>
        </Field>
        <Button
          variant="secondary"
          disabled={result.busy}
          onClick={result.refresh}
        >
          {t.retry}
        </Button>
      </div>
      {result.busy && <Loading />}
      <ErrorNotice error={result.error} />
      {result.data && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr>
                  {[
                    t.boxes,
                    t.status,
                    t.product,
                    t.quantity,
                    t.location,
                    t.condition,
                    t.version,
                  ].map((h) => (
                    <th className="p-2" key={h}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.data.boxes.map((box) => (
                  <tr className="border-t border-border" key={box.box_id}>
                    <td className="p-2">
                      <Button
                        variant="ghost"
                        onClick={() => onOpen(box.box_id)}
                      >
                        {box.box_id}
                      </Button>
                    </td>
                    <td className="p-2">{statusText(t, box.status)}</td>
                    <td className="p-2">{box.product_id ?? "—"}</td>
                    <td className="p-2">{box.quantity ?? "—"}</td>
                    <td className="p-2">{box.location_name ?? "—"}</td>
                    <td className="p-2">
                      {conditionText(t, box.condition)}{" "}
                      <code className="text-xs">{box.condition}</code>
                    </td>
                    <td className="p-2">{box.row_version}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {result.data.boxes.length === 0 && <p>{t.empty}</p>}
          <Pager
            offset={offset}
            total={result.data.total}
            limit={50}
            onChange={setOffset}
            busy={result.busy}
          />
        </>
      )}
    </Panel>
  );
}
export function InventoryStock() {
  const t = useInventoryText();
  const [product, setProduct] = useState<InventoryProduct | null>(null);
  const [policyPending, setPolicyPending] = useState(false);
  return (
    <Panel title={t.stock}>
      <ProductPicker disabled={policyPending} onSelect={setProduct} />
      {product && (
        <ProductStock
          key={product.product_id}
          product={product}
          onPending={setPolicyPending}
        />
      )}
    </Panel>
  );
}
function ProductStock({
  product,
  onPending,
}: {
  product: InventoryProduct;
  onPending: (pending: boolean) => void;
}) {
  const t = useInventoryText();
  const [uncertain, setUncertain] = useState(false);
  const stock = useRead(
    () => stockForProduct(product.product_id),
    [product.product_id],
  );
  const policy = useRead(
    () => getSerialPolicy(product.product_id),
    [product.product_id],
  );
  // The approval stays mounted while read caches are invalidated; its pending
  // write identity must not be recreated by Refresh or a policy-version key.
  const refresh = () => {
    stock.refresh();
    policy.refresh();
  };
  const dimensions = [
    ["on_hand", t.onHand],
    ["quarantined", t.quarantined],
    ["reserved", t.reserved],
    ["available", t.available],
    ["coverage", t.coverage],
  ] as const;
  return (
    <div className="space-y-4">
      <h3>{product.full_name || product.name}</h3>
      <Button
        variant="secondary"
        disabled={stock.busy || policy.busy}
        onClick={refresh}
      >
        {t.retry}
      </Button>
      {stock.busy && <Loading />}
      <ErrorNotice error={stock.error} />
      {stock.data && !stock.busy && !stock.error && !uncertain && (
        <>
          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-5">
            {dimensions.map(([key, label]) => (
              <div key={key} className="rounded border border-border p-3">
                <dt className="text-sm text-fg-secondary">{label}</dt>
                <dd className="font-mono text-lg">{stock.data![key] ?? "—"}</dd>
              </div>
            ))}
          </dl>
          {stock.data.coverage === "UNKNOWN" && (
            <p className="text-amber-600">{t.unknownCoverage}</p>
          )}
        </>
      )}
      <ErrorNotice error={policy.error} />
      {policy.busy && <Loading />}
      {policy.data && !uncertain && (
        <p>
          {t.serialPolicy}: {policyText(t, policy.data.mode)} · {t.version}:{" "}
          {policy.data.policy_version}
        </p>
      )}
      <PolicyApproval
        policy={policy.data}
        disabled={!policy.data || policy.busy || !!policy.error}
        onUncertain={() => {
          setUncertain(true);
          onPending(true);
        }}
        onChanged={() => {
          refresh();
          setUncertain(false);
          onPending(false);
        }}
      />
    </div>
  );
}
